import React, { Component, CSSProperties, MouseEventHandler } from 'react';
import classNames from "classnames";
import { Location, RangeReport, Size } from "../utils/types";
import { gridLocations, loc2Str } from "../boardgen/graphUtils";
import { DDBoardSpec } from "../boardgen/ddBoardgen";
import { MutableGrid } from "../utils/mutableGrid";
import { getStoredBool, markAsSolved } from "../utils/localStorage";
import { logDebug } from "../utils/logDebug";
import '../css/playBoard.css';
import '../css/monsters.css';

export type PlayBoardProps = {
    spec: DDBoardSpec,
    lockWhenSolved: boolean,
    onBoardChange?: () => void,
};

type PlayBoardState = {
    assignedWalls: MutableGrid,
    assignedFloors: MutableGrid,
};

type BlockState = 'immutable' | 'user-untouched' | 'user-wall' | 'user-floor';

type MouseBehaviour = {
    initialButtons: number,
    initialBlockState: BlockState,
    mouseAction: MouseAction,
}

type MouseAction = {
    useruntouched?: BlockState,
    userwall?: BlockState,
    userfloor?: BlockState,
}

type OverFlowCounter = {
    rows: Set<number>;
    cols: Set<number>;
}

export class PlayBoard extends Component<PlayBoardProps, PlayBoardState> {
    undoStack: PlayBoardState[];
    stateBeforeClick?: PlayBoardState;
    mouseBehaviour?: MouseBehaviour;
    private solvedReported = false;

    constructor(props: PlayBoardProps) {
        super(props);
        this.undoStack = [];
        const size = props.spec.rules.size;
        this.state = {
            assignedWalls: new MutableGrid(size, false),
            assignedFloors: new MutableGrid(size, false),
        };
    }

    /** When the board size changes, reset grids before render to avoid
     *  out-of-bounds accesses on the old (smaller) grids. */
    static getDerivedStateFromProps(props: PlayBoardProps, state: PlayBoardState): Partial<PlayBoardState> | null {
        const newSize = props.spec.rules.size;
        if (state.assignedWalls.size.height !== newSize.height ||
            state.assignedWalls.size.width !== newSize.width) {
            return {
                assignedWalls: new MutableGrid(newSize, false),
                assignedFloors: new MutableGrid(newSize, false),
            };
        }
        return null;
    }

    render() {
        const { size, throneSpec } = this.props.spec.rules;
        const countdownCounters = getStoredBool('countdownCounters');
        const st = {
            '--board-height': size.height,
            '--board-width': size.width,
        } as CSSProperties;

        const solved = this.isSolved();

        const boardClasses = classNames({
            'play-board': true,
            'play-board--completed': solved,
            'play-board--incomplete': !solved,
            'play-board--colorful-counters': getStoredBool('colorfulLineCounters'),
        });

        const solutionWalls = this.props.spec.wallCounts;
        const userWalls = this.state.assignedWalls.profile(true);
        const userFloors = this.state.assignedFloors.profile(true);

        // Just a single pass to figure out which rows/cols are overfilled.
        const rows = new Set<number>();
        const cols = new Set<number>();
        for (let i = 0; i < size.height; i++) if (userWalls.rows[i] > solutionWalls.rows[i]) rows.add(i);
        for (let j = 0; j < size.width; j++) if (userWalls.cols[j] > solutionWalls.cols[j]) cols.add(j);
        const overflowCounts: OverFlowCounter = { cols: cols, rows: rows };

        return (<>

            <div className={boardClasses} style={st} key={'itstheboard'}>
                <div className={'play-board__grid'} key={'itsthegrid'}>
                    {this.columnHints(countdownCounters)}
                    {gridLocations(size).map((row, j) => {
                        return <React.Fragment key={`row-${j}`}>
                            <div className={this.counterClasses('row', j)}
                                key={`rowhint${j}`}>
                                {solutionWalls.rows[j] - (countdownCounters ? this.state.assignedWalls.countTruesInRow(j) : 0)}

                                {/*<p className={'play-board__count__text'}> {wallCounts.rows[i]}</p>*/}
                            </div>
                            {row.map(loc =>
                                <div className={this.blockSquareClassnames(loc, overflowCounts)}
                                    key={loc2Str(loc)}
                                    onMouseDown={this.mouseDownFn(loc)}
                                    onMouseEnter={(e) => {
                                        if (e.buttons === this.mouseBehaviour?.initialButtons)
                                            this.performClickBehaviour(loc);
                                    }}
                                >
                                </div>
                            )}
                        </React.Fragment>;
                    }
                    )}
                </div>
            </div>
            <br />
        </>
        );
    }

    public reset(size?: Size) {
        // const {size} = this.props.spec.rules;
        if (size === undefined) size = this.props.spec.rules.size;
        this.undoStack = [];
        this.solvedReported = false;
        this.setState({
            assignedFloors: new MutableGrid(size, false),
            assignedWalls: new MutableGrid(size, false)
        }, () => this.props.onBoardChange?.());
    }

    mouseUpGlobal = (e: MouseEvent) => {
        // console.log('mouseUpGlobal fired');
        if (!this.stateBeforeClick) return;
        const changed = !this.stateBeforeClick.assignedFloors.equals(this.state.assignedFloors) ||
            !this.stateBeforeClick.assignedWalls.equals(this.state.assignedWalls);
        if (changed) {
            this.undoStack.push(this.stateBeforeClick);
            this.props.onBoardChange?.();
        }
        this.mouseBehaviour = undefined;
        this.stateBeforeClick = undefined;
    }

    // Disable default right click behaviour.
    onContextMenu = (e: MouseEvent) => e.preventDefault();

    // Capture Z for undo and R for reset.
    keyPress = (e: KeyboardEvent) => {
        // You can't undo while clicking stuff. That's just weird.
        if (this.mouseBehaviour) return;
        // console.log(e.code);
        if (e.code === 'KeyZ') this.attemptUndo();
        if (e.code === 'KeyR') this.reset();
    }

    componentDidMount(): void {
        // Cancel the dragged action we were thinking about.
        document.addEventListener('mouseup', this.mouseUpGlobal);
        // Global prevent context menu, so we can use right click without a bunch of trash showing up on screen.
        document.addEventListener('contextmenu', this.onContextMenu);
        // For the undo button.
        document.addEventListener('keypress', this.keyPress);
    }

    componentWillUnmount(): void {
        document.removeEventListener('mouseup', this.mouseUpGlobal);
        document.removeEventListener('contextmenu', this.onContextMenu);
        document.removeEventListener('keypress', this.keyPress);
    }

    componentDidUpdate(prevProps: PlayBoardProps): void {
        const prevSize = prevProps.spec.rules.size;
        const newSize = this.props.spec.rules.size;
        if (prevSize.height !== newSize.height || prevSize.width !== newSize.width) {
            this.undoStack = [];
        }

        // Reset the solved flag when a new puzzle loads.
        if (prevProps.spec !== this.props.spec) {
            this.solvedReported = false;
        }

        // Mark as solved exactly once per puzzle.
        if (!this.solvedReported && this.isSolved()) {
            this.solvedReported = true;
            markAsSolved(this.props.spec, Math.floor(Date.now()));
        }
    }

    public attemptUndo() {
        const p = this.undoStack.pop();
        logDebug(`undo`);
        if (p) {
            this.setState(p);
            this.solvedReported = false;
            this.props.onBoardChange?.();
        }
    }

    /** Load a wall configuration directly onto the board (dev tool). */
    public loadSolution(walls: MutableGrid) {
        this.undoStack = [];
        this.setState({
            assignedWalls: walls.copy(),
            assignedFloors: new MutableGrid(walls.size, false),
        }, () => this.props.onBoardChange?.());
    }

    // Set the sort of action the mouse held down over other elements will produce, depending on the status of the
    counterClasses(orientation: 'row' | 'col', order: number) {
        const report = this.rangeReport(orientation, order);
        const symbolCount = report.treasureCount + report.deadEndCount;

        // console.log(report)

        return classNames({
            'play-board__count': true,
            [`play-board__count--${orientation}`]: true,
            'play-board__count--undersatisfied': report.userWallCount < report.requiredWalls,
            'play-board__count--satisfied': report.userWallCount === report.requiredWalls,
            'play-board__count--oversatisfied': report.userWallCount > report.requiredWalls || report.userFloorCount + report.treasureCount + report.deadEndCount > report.requiredFloors,
            'play-board__count--floor-saturated': report.userFloorCount + symbolCount + report.requiredWalls === report.size,
        });
    }

    private isSolved = () => this.state.assignedWalls.equals(this.props.spec.walls);

    private setBlockState(loc: Location, newState: BlockState | undefined) {
        // console.log(`setBlockState ${loc2Str(loc)} ${newState}`)
        const assignedWalls = this.state.assignedWalls;
        const assignedFloors = this.state.assignedFloors;

        const oldWall = assignedWalls.check(loc);
        const oldFloor = assignedFloors.check(loc);

        switch (newState) {
            case "user-floor":
                assignedWalls.setLoc(loc, false);
                assignedFloors.setLoc(loc, true);
                break;
            case "user-wall":
                assignedWalls.setLoc(loc, true);
                assignedFloors.setLoc(loc, false);
                break;
            case "user-untouched":
                assignedWalls.setLoc(loc, false);
                assignedFloors.setLoc(loc, false);
        }

        // Call for an update if we did anything.
        if (assignedWalls.check(loc) !== oldWall || assignedFloors.check(loc) !== oldFloor)
            this.forceUpdate();
    }

    rangeReport(orientation: 'row' | 'col', order: number) {
        let requiredWalls, requiredFloors, userWalls, userFloors, size, treasureCounts, deadEndCounts;
        if (orientation === 'row') {
            requiredWalls = this.props.spec.wallCounts.rows;
            requiredFloors = this.props.spec.floorCounts.rows;
            size = this.props.spec.rules.size.width;
            userWalls = this.state.assignedWalls.profile(true).rows;
            userFloors = this.state.assignedFloors.profile(true).rows;
            treasureCounts = this.props.spec.treasure.profile(true).rows;
            deadEndCounts = this.props.spec.deadends.profile(true).rows;
        } else if (orientation === 'col') {
            requiredWalls = this.props.spec.wallCounts.cols;
            requiredFloors = this.props.spec.floorCounts.cols;
            size = this.props.spec.rules.size.height;
            userWalls = this.state.assignedWalls.profile(true).cols;
            userFloors = this.state.assignedFloors.profile(true).cols;
            treasureCounts = this.props.spec.treasure.profile(true).cols;
            deadEndCounts = this.props.spec.deadends.profile(true).cols;
        } else throw new Error(`Shouldn't happen ever`);

        return {
            order: order,
            orientation: orientation,
            size: size,
            requiredWalls: requiredWalls[order],
            requiredFloors: requiredFloors[order],
            userWallCount: userWalls[order],
            userFloorCount: userFloors[order],
            deadEndCount: deadEndCounts[order],
            treasureCount: treasureCounts[order],
        } as RangeReport;
    }

    // Whether the player should ba allowed to change the appearance of a square in any way while playing.
    isImmutable(loc: Location) {
        const { deadends, treasure } = this.props.spec;
        return deadends.check(loc) || treasure.check(loc);
    }

    blockState(loc: Location): BlockState {
        if (this.isImmutable(loc)) return 'immutable';
        if (this.state.assignedWalls.check(loc)) return 'user-wall';
        if (this.state.assignedFloors.check(loc)) return 'user-floor';
        return "user-untouched";
    }

    blockSquareClassnames = (loc: Location, overflows: OverFlowCounter) => {
        const { assignedWalls, assignedFloors } = this.state;
        const isUserFloor = assignedFloors.check(loc);
        const isUserWall = assignedWalls.check(loc);

        const { floors, deadends, treasure, monsterChoices } = this.props.spec;
        const isDeadend = deadends.check(loc);
        const isTreasure = treasure.check(loc);
        const isImmutable = this.isImmutable(loc);
        const monstername = `block-square--monster${monsterChoices.get(loc2Str(loc))}`;
        const isOverflow = overflows.cols.has(loc.x) || overflows.rows.has(loc.y)

        return classNames({
            'block-square': true,
            'block-square--user-wall': isUserWall,
            'block-square--user-floor': isUserFloor,
            'block-square--user-untouched': !isImmutable && !isUserFloor && !isUserWall,
            'block-square--immutable': isImmutable,
            'block-square--deadend': isDeadend,
            'block-square--overflow': isOverflow,
            [monstername]: isDeadend,
            'block-square--treasure': isTreasure,
        });
    }

    private performClickBehaviour(loc: Location) {
        if (!this.mouseBehaviour) {
            logDebug(`performBehaviour with no mouseBehaviour set`);
            return;
        }
        // Can't monkey with a solved puzzle.
        if (this.props.lockWhenSolved && this.isSolved()) return;

        let newState: BlockState | undefined;
        switch (this.blockState(loc)) {
            case "user-wall":
                newState = this.mouseBehaviour.mouseAction.userwall;
                break;
            case "user-floor":
                newState = this.mouseBehaviour.mouseAction.userfloor;
                break;
            case "user-untouched":
                newState = this.mouseBehaviour.mouseAction.useruntouched;
                break;
        }
        this.setBlockState(loc, newState);
    }

    // square it is over when the mouse if first pressed.
    private mouseDownFn(loc: Location): MouseEventHandler<HTMLDivElement> {
        return (e) => {
            // 2nd click does nothing.
            if (this.stateBeforeClick) return;

            let action: MouseAction = {};
            const initialButtons = e.buttons;
            const initialBlockState = this.blockState(loc);

            // console.log(`Mousedown inner buttons ${e.buttons}`)

            if (initialButtons === 1) {
                // Left click
                switch (initialBlockState) {
                    case "user-untouched":
                        action = { useruntouched: 'user-wall', }
                        break;
                    case "user-floor":
                        action = { useruntouched: 'user-wall', userfloor: 'user-wall' }
                        break;
                    case "user-wall":
                        action = { userwall: 'user-untouched', userfloor: 'user-untouched' }
                }
            } else if (initialButtons === 2) {
                // Right click
                switch (initialBlockState) {
                    case "user-untouched":
                        action = { useruntouched: 'user-floor', }
                        break;
                    case "user-wall":
                        action = { userwall: 'user-untouched', userfloor: 'user-untouched' }
                        break;
                    case "user-floor":
                        action = { userwall: 'user-untouched', userfloor: 'user-untouched' }
                        break;
                }
            }

            this.stateBeforeClick = {
                assignedWalls: this.state.assignedWalls.copy(),
                assignedFloors: this.state.assignedFloors.copy(),
            }
            this.mouseBehaviour = {
                initialButtons: initialButtons,
                initialBlockState: initialBlockState,
                mouseAction: action,
            }
            this.performClickBehaviour(loc);
        }
    }

    private columnHints(countdownCounters: boolean) {
        const solutionWalls = this.props.spec.wallCounts;

        return <>
            <div className={'play-board--topcorner'} key={'topcorner'} />
            {solutionWalls.cols.map((cnt, i) =>
                <div className={this.counterClasses('col', i)}
                    key={`colhint${i}`}>
                    {cnt - (countdownCounters ? this.state.assignedWalls.countTruesInColumn(i) : 0)}
                    {/*<p className={'play-board__count__text'}> {cnt}</p>*/}
                </div>)}

        </>
    }


}
